import { CLIMATE, TERRAIN } from '../config';
import { fbm, clamp01 } from './noise';

// The SHARED climate noise lanes (temperature + moisture), equalized PER SEED. Both biome.ts (paint)
// and terrain.ts (arid flattening) read these, so the desert stays flat exactly where the desert paints.
//
// Why equalization exists (owner bug 2026-07-10): the raw fbm lanes have features on the order of the
// whole island, so one seed lands a single hot/dry lobe over everything (an all-olive map) and the next
// buries half the island under one cold slab (all-grey). The old CLIMATE.contrast stretch made this
// WORSE -- it pushed whichever lobe you got to an extreme. Instead, each seed now samples its own raw
// field across the island disc once and remaps every value through that distribution (its quantile
// rank), so hot/cold/wet/dry always occur in the SAME even proportions on every seed -- only the
// LAYOUT changes. Deterministic (same samples on every client), cheap (~1400 fbm calls, once per seed),
// and re-runs automatically when the seed or the frequency knobs change (the F3 panel keys included).

function rawTemp(x: number, z: number): number {
  const f = CLIMATE.tempFreq / TERRAIN.cell;
  return fbm(x * f, z * f, 3, 0.5, 2.0, CLIMATE.seed + 11);
}
function rawMoist(x: number, z: number): number {
  const f = CLIMATE.moistFreq / TERRAIN.cell;
  return fbm(x * f, z * f, 3, 0.5, 2.0, CLIMATE.seed + 23);
}
function rawIrrad(x: number, z: number): number {
  const f = CLIMATE.irradFreq / TERRAIN.cell;
  return fbm(x * f, z * f, 2, 0.5, 2.0, CLIMATE.seed + 37);
}

let calibKey = '';
let tQ = new Float32Array(0);
let mQ = new Float32Array(0);
let iQ = new Float32Array(0);

// Sample both raw lanes across the island disc and keep the sorted values as quantile LUTs. Keyed on
// every input that changes the raw field, so seed swaps (menu map previews bake OTHER worlds' seeds by
// swapping the globals) and live F3 frequency edits recalibrate transparently.
function calibrate(): void {
  const key = `${CLIMATE.seed}|${CLIMATE.tempFreq}|${CLIMATE.moistFreq}|${CLIMATE.irradFreq}|${TERRAIN.cell}|${TERRAIN.islandInner}`;
  if (key === calibKey) return;
  calibKey = key;
  const R = TERRAIN.islandInner, N = 30;
  const ts: number[] = [], ms: number[] = [], is: number[] = [];
  for (let i = 0; i < N; i++) {
    for (let j = 0; j < N; j++) {
      const x = (((i + 0.5) / N) * 2 - 1) * R, z = (((j + 0.5) / N) * 2 - 1) * R;
      if (x * x + z * z > R * R) continue; // the island disc: ocean floor far outside it never paints a biome
      ts.push(rawTemp(x, z));
      ms.push(rawMoist(x, z));
      is.push(rawIrrad(x, z));
    }
  }
  ts.sort((a, b) => a - b);
  ms.sort((a, b) => a - b);
  is.sort((a, b) => a - b);
  tQ = Float32Array.from(ts);
  mQ = Float32Array.from(ms);
  iQ = Float32Array.from(is);
}

// v's quantile rank in the sorted LUT (0..1): binary search for the insertion point.
function rank(q: Float32Array, v: number): number {
  const n = q.length;
  if (n < 2) return 0.5;
  let lo = 0, hi = n;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (q[mid] < v) lo = mid + 1; else hi = mid; }
  return lo / (n - 1);
}

// Equalized temperature lane, 0 cold .. 1 hot, uniformly distributed across the island on EVERY seed.
// (Callers layer latitude/altitude shifts on top -- see biome.temperature.)
export function tempField(x: number, z: number): number {
  calibrate();
  return clamp01(rank(tQ, rawTemp(x, z)));
}

// Equalized moisture lane, 0 dry .. 1 wet, uniformly distributed across the island on EVERY seed.
export function moistField(x: number, z: number): number {
  calibrate();
  return clamp01(rank(mQ, rawMoist(x, z)));
}

// Equalized radioactive-blight lane. CLIMATE.irradThresh is a QUANTILE against this: 0.93 means the top
// 7% of the island is Irradiated on every seed. (The raw lane's one giant lobe used to cross the old
// absolute threshold on some seeds and blight 80% of the map -- the owner's "all-olive island" bug.)
export function irradField(x: number, z: number): number {
  calibrate();
  return clamp01(rank(iQ, rawIrrad(x, z)));
}
